import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import * as authService from '../services/authService';
import * as notificationsService from '../services/appNotificationsService';
import * as plansService from '../services/plansService';
import Loading from '../components/common/Loading';
import { setCookie } from 'common/sessionUtils';
import * as usersService from 'services/usersService';

const AuthContext = createContext<any>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [doctor, setDoctor] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const queryClient = useQueryClient();

  // Pings the backend's current-plan endpoint, which self-heals stale
  // Apple/Braintree subscriptions server-side (see PlanComponent.get_user_plan).
  // Fire-and-forget: never blocks login/app-open on this.
  const refreshPlanState = () => {
    queryClient.fetchQuery(['current-plan'], plansService.getCurrentPlan)
      .catch((e) => console.log('Refresh plan state error', e));
  };

  const login = (userData: any) => {
    setUser((prev: any) => {
      const resolvedUser =
        typeof userData === 'function' ? userData(prev) : userData;

      saveUser(resolvedUser);
      setCookie('user', JSON.stringify(resolvedUser));

      return resolvedUser;
    });

    notificationsService.registerToken();
    refreshPlanState();
  };

  const setScope = (doctor: any) => {
    localStorage.setItem('doctor', JSON.stringify(doctor));
    setDoctor(doctor);
  };

  const logout = async () => {
    setUser(null);
    setDoctor(null);
    try {
      await authService.unRegisterForPushNotifications();
      localStorage.removeItem('@user');
      localStorage.removeItem('doctor');
    } catch (e) {
      console.log('Logout error', e);
    }
  };

  const saveUser = async (user: any) => {
    try {
      const jsonUser = JSON.stringify(user);
      localStorage.setItem('@user', jsonUser);
    } catch (e) {
      console.log('Save user error', e);
    }
  };


  const loadUser = async (): Promise<any> => {
    try {
      const storedUser = localStorage.getItem('@user');
      if (!storedUser) return null;

      const parsedUser = JSON.parse(storedUser);

      const newUser = await usersService.getUserById(parsedUser.id);

      const updatedUser = {
        ...parsedUser,
        profile_image: newUser.profile_image,
      };

      saveUser(updatedUser);
      setCookie('user', JSON.stringify(updatedUser));

      return updatedUser;
    } catch (e) {
      console.log('Load user error', e);
      return null;
    }
  };

  const loadDoctor = () => {
    try {
      const doctor = localStorage.getItem('doctor');
      return doctor ? JSON.parse(doctor) : null;
    } catch (e) {
      console.log('Load doctor error', e);
      return null;
    }
  };

  useEffect(() => {
    const checkAuthState = async () => {
      const storedAuthState = await loadUser();
      if (storedAuthState) {
        setUser(storedAuthState);
        notificationsService.registerToken();
        refreshPlanState();
      }
      setLoading(false);
    };
    checkAuthState();
  }, []);

  useEffect(() => {
    const checkDoctorState = async () => {
      const storedDoctorState = await loadDoctor();
      if (storedDoctorState) {
        setDoctor(storedDoctorState);
      }
    };
    checkDoctorState();
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      login,
      logout,
      needsOnboarding,
      setNeedsOnboarding,
      doctor,
      setScope,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
